import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { type CalendarEventTypeWithHour, type EventType } from "@modules/calendar/calendarTypes"
import { CalendarDayColumn } from "@modules/calendar/components/calendarDayCol"
import { useCalendar } from "@modules/calendar/hooks/useCalendar"
import { CALENDAR_OPENING_HOURS, DEFAULT_CALENDAR_HOUR_HEIGHT } from "@modules/calendar/utils/calendarConstants"
import { calculateEventsPosition } from "@modules/calendar/utils/eventsHelpers"
import { word } from "@modules/core/utils/i18n"
import { getFormatedDateTime } from "@modules/language/languageUtils"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { getScrollbarWidth } from "@modules/utils/utils"
import dayjs, { Dayjs } from "dayjs"
import { EyeOff, Plus } from "lucide-react"
import { useCallback, useEffect, useMemo, useState } from "react"

type CalendarProps = {
  calendarEvents: EventType[]
  canAddEvents: boolean
  eventLimitPerDay?: number
  onAddEventClick?: (arg0?: Date | number) => void
  customRenderEvent?: (index: number, event: EventType, displayDetailedEvents?: boolean) => React.ReactNode
  onEventDragStop?: (dateDest?: dayjs.Dayjs, position?: number) => Promise<void>
  showNoAccessOverlay?: boolean
  noAccessText?: string
  noAccessButton?: { text: string; onClick: () => void }
  loading?: boolean
  isDragAndDropActive?: boolean
} & React.ComponentProps<"div">

export type EventPerDay = {
  dayIndex?: number
  label: string
  date?: dayjs.Dayjs
  events: EventType[]
  class: string
}

export function Calendar({
  calendarEvents,
  canAddEvents,
  eventLimitPerDay = 20,
  onAddEventClick,
  customRenderEvent,
  showNoAccessOverlay,
  noAccessText,
  noAccessButton,
  loading = false,
  onEventDragStop,
  isDragAndDropActive = false,
  className,
}: CalendarProps) {
  const [hasEventOnHoveredHour, setHasEventOnHoveredHour] = useState<{
    hour: number
    date: Dayjs | number
    hasEvents: boolean
  }>()
  const { currentWeekdays, isUndated, hasHours } = useCalendar()

  // Create an array containing the days of the week + their events
  const eventsPerDays: EventPerDay[] | undefined = useMemo(() => {
    return currentWeekdays?.map(({ date, isToday, dayIndex, label }) => ({
      dayIndex,
      label,
      date,
      events: hasHours
        ? calculateEventsPosition(
            calendarEvents.filter((event) =>
              "date" in event && event.date
                ? dayjs(event.date).isSame(date, "date")
                : "dayIndex" in event && event.dayIndex === dayIndex,
            ) as CalendarEventTypeWithHour[],
            canAddEvents ? 85 : 95,
          )
        : calendarEvents.filter((event) =>
            "date" in event && event.date
              ? dayjs(event.date).isSame(date, "date")
              : "dayIndex" in event && event.dayIndex === dayIndex,
          ),
      class: isToday ? "today" : date?.isAfter() || isUndated ? "future" : "past",
    }))
  }, [isUndated, currentWeekdays, calendarEvents, hasHours, canAddEvents])

  // Check if the user can create an event on a given day & hour
  const canUserCreateEvent = useCallback(
    (hour: number, date?: Dayjs, dayIndex?: number) => {
      return (
        canAddEvents &&
        onAddEventClick &&
        hasEventOnHoveredHour?.hour === hour &&
        (typeof hasEventOnHoveredHour.date === "number"
          ? hasEventOnHoveredHour.date === dayIndex
          : dayjs(date).isSame(hasEventOnHoveredHour.date, "date"))
      )
    },
    [canAddEvents, onAddEventClick, hasEventOnHoveredHour],
  )

  // Create an event on a given day & hour
  const createEventOnDayAndHour = (hour: number, date?: Dayjs, dayIndex?: number) => {
    if (onAddEventClick) {
      if (dayIndex !== undefined) {
        onAddEventClick(dayjs().weekday(dayIndex).hour(hour).minute(0).second(0).millisecond(0).toDate())
      } else {
        onAddEventClick(dayjs(date).hour(hour).minute(0).second(0).millisecond(0).toDate())
      }
    }
  }

  // Function to check if there's events on a given hour (the hovered one)
  const checkIfHasEventOnHoveredHour = useCallback(
    (hour: number, date: Dayjs | number) => {
      const result = calendarEvents
        .filter((event) =>
          typeof date !== "number"
            ? "date" in event && dayjs(date).isSame(event.date, "date")
            : "dayIndex" in event && date === event.dayIndex,
        )
        .filter((event) => "startDate" in event)
        .some((event) => {
          if (!("endDate" in event) || !("startDate" in event)) return false
          const eventBeginningHour = dayjs(event.startDate).hour()
          const eventEndingHour = dayjs(event.endDate).hour()
          const eventEndingMinute = dayjs(event.endDate).minute()

          return (
            eventBeginningHour === hour ||
            (eventEndingHour === hour && eventEndingMinute > 0) ||
            (eventBeginningHour < hour && eventEndingHour > hour)
          )
        })
      setHasEventOnHoveredHour({ hour, date, hasEvents: result })
    },
    [calendarEvents],
  )

  // Check if we should change the scroll to fit the earliest event of the week
  useEffect(() => {
    if (hasHours) {
      let earliestEventOfTheWeekPosition: number | null = null
      const shouldCheckOverlay = eventsPerDays?.some(
        (eventPerDays) => eventPerDays.events.findIndex((event) => "isOverlayed" in event && event.isOverlayed) >= 0,
      )

      eventsPerDays?.forEach((eventPerDays) => {
        eventPerDays.events.forEach((event) => {
          if (
            "style" in event &&
            typeof event.style?.top !== "undefined" &&
            ((shouldCheckOverlay && "isOverlayed" in event && event.isOverlayed) || !shouldCheckOverlay) &&
            (earliestEventOfTheWeekPosition === null || event.style.top < earliestEventOfTheWeekPosition)
          )
            earliestEventOfTheWeekPosition = event.style.top
        })
      })

      if (
        earliestEventOfTheWeekPosition &&
        earliestEventOfTheWeekPosition - 10 > (document.getElementById("scrollable-view")?.scrollTop ?? 0)
      )
        document.getElementById("scrollable-view")?.scrollTo({ top: earliestEventOfTheWeekPosition - 10 })
    }
  }, [hasHours, JSON.stringify(eventsPerDays)])

  return (
    <div className={cn("calendar relative flex w-full text-center", className)}>
      {hasHours ? (
        <div className="w-full">
          {/* Displays days of the week */}
          <div className="ml-8.5 flex flex-col" style={{ marginRight: getScrollbarWidth() }}>
            <div className="flex flex-row">
              {currentWeekdays?.map((weekday) => (
                <div
                  className={`weekday-label weekday-label-${weekday.weekdayIndex.toString()} flex flex-1 flex-col justify-center ${weekday.isToday ? "today" : ""}`}
                  key={weekday.weekdayIndex}
                >
                  {weekday.label}
                </div>
              ))}
            </div>
          </div>

          <div id="scrollable-view" className="flex w-full flex-row overflow-y-scroll" style={{ height: 600 }}>
            {/* Displays hours of the day */}
            <div className="flex flex-col">
              {CALENDAR_OPENING_HOURS.map((hour, hourIndex) => (
                <Text
                  fontStyle="LS"
                  key={hourIndex}
                  className="opening-hour w-8.5"
                  style={{ minHeight: DEFAULT_CALENDAR_HOUR_HEIGHT }}
                >
                  {getFormatedDateTime(dayjs().set("hour", hour).set("minute", 0), { mode: "time" })}
                </Text>
              ))}
            </div>

            {/* Map on each day of the week */}
            {eventsPerDays?.map((eventsPerDay, eventsPerDayIndex) => (
              <div className="relative flex w-full flex-col" key={eventsPerDayIndex}>
                {/* Map on each hour of the day */}
                {CALENDAR_OPENING_HOURS.map((hour, hourIndex) => (
                  <div
                    key={hourIndex}
                    className={"timeslot-container " + "timeslot-" + eventsPerDayIndex.toString()}
                    style={{ minHeight: DEFAULT_CALENDAR_HOUR_HEIGHT }}
                    onMouseEnter={() => {
                      if (typeof eventsPerDay.date !== "undefined")
                        checkIfHasEventOnHoveredHour(hour, eventsPerDay.date)
                      else if (typeof eventsPerDay.dayIndex !== "undefined")
                        checkIfHasEventOnHoveredHour(hour, eventsPerDay.dayIndex)
                    }}
                    onMouseLeave={() => setHasEventOnHoveredHour(undefined)}
                  >
                    {/* Render the "Add Event" button */}
                    {hasEventOnHoveredHour &&
                      canUserCreateEvent(hour, eventsPerDay.date, eventsPerDay.dayIndex) &&
                      (hasEventOnHoveredHour.hasEvents ? (
                        <>
                          <div
                            className="add-button-small"
                            onClick={() => createEventOnDayAndHour(hour, eventsPerDay.date, eventsPerDay.dayIndex)}
                          >
                            <Plus className="size-4" />
                          </div>
                        </>
                      ) : (
                        <>
                          <div
                            className="flex cursor-pointer flex-row items-center justify-center gap-2 px-5 py-2"
                            onClick={() => createEventOnDayAndHour(hour, eventsPerDay.date, eventsPerDay.dayIndex)}
                          >
                            <Text fontStyle="LM">
                              {typeof eventsPerDay.date !== "undefined"
                                ? word("programmation.slot.create")
                                : word("programmation.slotTemplates.create_slotTemplate")}
                            </Text>
                            <Button variant="outline" size="iconMd">
                              <Plus />
                            </Button>
                          </div>
                        </>
                      ))}
                  </div>
                ))}

                {/* Map and render the events of current day */}
                {eventsPerDay.events.map(
                  (event, eventIndex) => "style" in event && customRenderEvent?.(eventIndex, event),
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* Calendar with no hour section */
        <table className="calendar-table">
          {/* Displays days of the week */}
          <thead>
            <tr>
              {currentWeekdays?.map((weekday) => (
                <th
                  className={`weekday-label weekday-label-${weekday.weekdayIndex.toString()} ${weekday.isToday ? "today" : ""}`}
                  key={weekday.weekdayIndex}
                >
                  <Text fontStyle="TS" className="font-bold">
                    {weekday.label}
                  </Text>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {/* Map on each day of the week and render each related events */}
              {!loading &&
                eventsPerDays?.map((eventsPerDay, index) => (
                  <CalendarDayColumn
                    key={index}
                    index={index}
                    eventsPerDay={eventsPerDay}
                    customRenderEvent={customRenderEvent}
                    onAddEventClick={onAddEventClick}
                    canAddEvents={canAddEvents}
                    eventLimitPerDay={eventLimitPerDay}
                    isDragAndDropActive={isDragAndDropActive}
                    onEventDragStop={onEventDragStop}
                  />
                ))}
            </tr>
          </tbody>
        </table>
      )}
      {/* Loading state */}
      {loading && (
        <div className="no-access-overlay">
          <LoadingSpinner />
        </div>
      )}
      {/* No access overlay section */}
      {!loading && showNoAccessOverlay && noAccessText && (
        <div className="no-access-overlay">
          <EyeOff size={24} />
          <Text fontStyle="TM">{noAccessText}</Text>
          {noAccessButton && (
            <Button variant="primary" className="mt-2" onClick={() => noAccessButton.onClick()}>
              {noAccessButton.text}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
